import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import {
  DataTable,
  ExportCsvButton,
  FilterBar,
  MiniBars,
  Section,
  SelectFilter,
  StatCard,
} from "@/components/admin/admin-kit";
import { admAnalytics } from "@/lib/admin-pro.functions";
import { formatHeat, formatMoney } from "@/lib/behot";
import { privatePageMetadata } from "@/lib/seo";

export const Route = createFileRoute("/nc/live/adm/analytics")({
  head: () => privatePageMetadata("/nc/live/adm/analytics", "Analytics | Control room", "Internal analytics."),
  component: AdminAnalytics,
});

const RANGES = [
  { value: "7", label: "Last 7 days" },
  { value: "30", label: "Last 30 days" },
  { value: "90", label: "Last 90 days" },
  { value: "365", label: "Last 12 months" },
] as const;

function AdminAnalytics() {
  const run = useServerFn(admAnalytics);
  const [range, setRange] = useState<(typeof RANGES)[number]["value"]>("30");

  const { data, isLoading } = useQuery({
    queryKey: ["adm-analytics", range],
    queryFn: () => run({ data: { days: Number(range) } }),
  });

  if (isLoading || !data) return <p className="text-sm text-muted-foreground">Loading analytics…</p>;

  const t = data.totals;

  return (
    <div className="space-y-6">
      <FilterBar>
        <SelectFilter value={range} onChange={setRange} options={RANGES} label="Range" />
        <ExportCsvButton rows={data.series} filename="behot-analytics" />
      </FilterBar>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Board revenue" value={formatMoney(t.revenueCents)} hint={`${t.orders} orders`} tone="hot" />
        <StatCard label="Marketplace volume" value={formatMoney(t.mpRevenueCents)} />
        <StatCard label="New users" value={t.signups} hint={`${t.referrals} referrals`} tone="ok" />
        <StatCard label="Boosts placed" value={t.boosts} />
        <StatCard label="Referral rewards" value={`${formatHeat(t.referralRewardCents)} Heat`} />
        <StatCard
          label="Pending payouts"
          value={formatMoney(t.pendingWithdrawalCents)}
          tone={t.pendingWithdrawalCents > 0 ? "warn" : "default"}
        />
        <StatCard
          label="Top-up credited"
          value={formatMoney(data.ledgerByWallet['topup']?.creditCents ?? 0)}
        />
        <StatCard label="Earn credited" value={formatMoney(data.ledgerByWallet['earn']?.creditCents ?? 0)} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Section title="Revenue per day" description="Paid board orders">
          <MiniBars data={data.series} valueKey="revenueCents" format={(n) => formatMoney(n)} />
        </Section>
        <Section title="Signups per day">
          <MiniBars data={data.series} valueKey="signups" />
        </Section>
        <Section title="Boosts per day">
          <MiniBars data={data.series} valueKey="boosts" />
        </Section>
        <Section title="Marketplace volume per day">
          <MiniBars data={data.series} valueKey="mpRevenueCents" format={(n) => formatMoney(n)} />
        </Section>
      </div>

      <Section title="Heat by category" description="Active boost value per board">
        <DataTable
          rows={data.topCategories}
          rowKey={(r) => r.id}
          columns={[
            { key: "name", header: "Category", cell: (r) => r.name, sortValue: (r) => r.name },
            { key: "status", header: "Status", cell: (r) => r.status },
            {
              key: "heat",
              header: "Active Heat",
              cell: (r) => `${formatHeat(r.heatCents)} Heat`,
              sortValue: (r) => r.heatCents,
            },
            { key: "value", header: "Value", cell: (r) => formatMoney(r.heatCents), sortValue: (r) => r.heatCents },
          ]}
        />
      </Section>

      <div className="grid gap-4 lg:grid-cols-2">
        <Section title="Top listings by clicks">
          <DataTable
            rows={data.topListings}
            rowKey={(r) => r.id}
            columns={[
              { key: "name", header: "Listing", cell: (r) => r.name, sortValue: (r) => r.name },
              { key: "status", header: "Status", cell: (r) => r.moderation_status },
              {
                key: "clicks",
                header: "Clicks",
                cell: (r) => Number(r.click_count ?? 0).toLocaleString(),
                sortValue: (r) => Number(r.click_count ?? 0),
              },
            ]}
          />
        </Section>
        <Section title="Top marketplace services">
          <DataTable
            rows={data.topServices}
            rowKey={(r) => r.id}
            columns={[
              { key: "title", header: "Service", cell: (r) => r.title, sortValue: (r) => r.title },
              { key: "status", header: "Status", cell: (r) => String(r.status) },
              {
                key: "orders",
                header: "Orders",
                cell: (r) => Number(r.orders_count ?? 0),
                sortValue: (r) => Number(r.orders_count ?? 0),
              },
              {
                key: "views",
                header: "Views",
                cell: (r) => Number(r.view_count ?? 0),
                sortValue: (r) => Number(r.view_count ?? 0),
              },
            ]}
          />
        </Section>
      </div>
    </div>
  );
}
